import { getGroundArea, getSpecieVolume, simulate } from "@foresting-tomorrow/simulation";
import dayjs from "dayjs";
import { isLeft } from "fp-ts/lib/Either";

// Project import
import { FirestoreStandProps } from "types/firestoreFeatures";
import { EstimationModels, ThinningData } from "types/simulation";
import { SimulateSpeciesProps } from "types/stand";

// Parameters
const DEFUALT_FOLIAGE_SPC_MODEL_TYPE = "Ahorn - ÆR";
const DEFUALT_NEEDLE_SPC_MODEL_TYPE = "Nobilis - NOB";

export function simulateSpecies({
  stand,
  horizonYears,
  autoThinningType,
  thinningDataPlanned,
  thinningStartAge,
  thinningInterval,
  thinningPercentage,
  speciesList,
}: SimulateSpeciesProps) {
  if (!speciesList) return;
  // Check horizon years
  const h = horizonYears ? horizonYears : 10;
  // Get simulation model type
  const data = mapStandDataToSimulationInput({ standData: stand, speciesList });
  if (!data) {
    // console.warn("Couldn't map stand data to simulation input", { stand });
    return null;
  }
  let thinningData: ThinningData = [];
  const thinPercentage = thinningPercentage ? thinningPercentage : 20;
  if (autoThinningType && autoThinningType === "interval") {
    const minThinStartAge = thinningStartAge ? thinningStartAge : 25;
    const thinInterval = thinningInterval ? thinningInterval : 5;
    let startYear = dayjs().year();
    // @ts-ignore
    if (data.age && data.age < minThinStartAge) {
      // @ts-ignore
      const diff = minThinStartAge - data.age;
      startYear = startYear + diff;
    }
    thinningData = createThinningArray(startYear, h, thinInterval, thinPercentage);
  } else if (autoThinningType === "planned" && thinningDataPlanned) {
    thinningData = thinningDataPlanned;
  }
  // Get quality parameter
  const result = simulate(
    data.species,
    [0, h],
    data.initCondition,
    thinningData,
    false,
    data.quality,
    true,
    autoThinningType === "groundArea",
    thinPercentage
  );
  return result;
}

type MapStandDataToSimulationInputProps = {
  standData: FirestoreStandProps;
  speciesList: { [key: string]: any };
  featureArea?: number;
};
function mapStandDataToSimulationInput({
  standData,
  speciesList,
  featureArea,
}: MapStandDataToSimulationInputProps) {
  // Unpack parameters
  const { species, year, height, diameter, treeCount, quality, mix } = standData;
  if (typeof year !== "number" || year <= 0) {
    // console.warn("Stand year not valid", year);
    return null;
  }
  if (typeof height !== "number" || height <= 0) {
    // console.warn("Height not valid", height);
    return null;
  }
  if (typeof diameter !== "number" || diameter <= 0) {
    // console.warn("Diameter not valid", diameter);
    return null;
  }
  if (typeof treeCount !== "number" || treeCount <= 0) {
    // console.warn("Tree Count not valid", treeCount);
    return null;
  }
  // Calculate ground area and volume
  const speciesModelType = getSpeciesModelType(species, speciesList);
  const volume =
    getSpecieVolume(speciesModelType, { d: diameter / 100, h: height, T: 0, a: 0 }).vb * treeCount;

  const groundArea = getGroundArea(diameter / 100, treeCount);
  if (isLeft(groundArea)) {
    console.warn("Couldn't calculate ground area", groundArea.left);
    return null;
  }
  // Calculate age
  const age = new Date().getFullYear() - year;

  // Check quality
  let q = quality ? quality : 2;
  if (q < 1) q = 1;
  if (q > 5) q = 5;

  // Create initial condition
  const initCondition = {
    area: featureArea ? featureArea : 1,
    age: age,
    height: height,
    diameter: diameter / 100,
    treeCount: treeCount,
    volume: isNaN(volume) ? 0.1 : volume,
    groundArea: groundArea.right,
  };
  return {
    species: speciesModelType,
    initCondition,
    quality: q,
  };
}

function getSpeciesModelType(species: string, speciesList: { [key: string]: any }) {
  let modelType = DEFUALT_NEEDLE_SPC_MODEL_TYPE;
  if (getSpeciesType(species, speciesList) === "foliage")
    modelType = DEFUALT_FOLIAGE_SPC_MODEL_TYPE;
  try {
    modelType = speciesList[species].estModel
      ? (speciesList[species].estModel as string)
      : modelType;
  } catch (error) {
    console.warn("Model type not recognised", { error, species });
  }
  return modelType as EstimationModels;
}

export function getSpeciesType(species: string, speciesList: { [key: string]: any }) {
  let type = "other";
  try {
    type = speciesList[species].type;
  } catch (error) {
    console.warn("Type not recognised", { error, species });
  }
  return type;
}

function createThinningArray(
  startYear: number,
  horizon: number,
  thinningInterval: number,
  thinningPercentage: number,
  randomProperbility?: number
) {
  const thinningData: ThinningData = [];
  let thinningYear = startYear + thinningInterval;
  const rdp = randomProperbility ? randomProperbility : 0;
  while (thinningYear < startYear + horizon) {
    if (Math.random() > rdp) {
      thinningData.push({
        date: `${thinningYear}`,
        percentageThinning: thinningPercentage,
        treeCountThinning: 0,
        volumeThinning: 0,
      });
    }
    thinningYear += thinningInterval;
  }
  return thinningData;
}
